import classNames from 'classnames'
import { GatsbyImage } from 'gatsby-plugin-image'
import React, { ReactNode, useRef, useState } from 'react'
import useClickOutside from '../../hooks/useClickOutside'
import { resolveAsImage } from '../../resolvers'
import { GatsbyResolvedAsset } from '../../types/image'
import Caption from '../Caption/Caption'
import Dialog from '../Dialog/Dialog'
import FreeText from '../FreeText/FreeText'
import Heading from '../Heading/Heading'
import styles from './TeamMember.module.scss'

export type TeamMemberProps = {
  id: string
  firstName: string
  lastName: string
  bio?: ReactNode
  title?: string
  picture: GatsbyResolvedAsset
  direction?: 'vertical' | 'horizontal'
  className?: string
  hasDialog?: boolean
  hideInfo?: boolean
}
export default function TeamMember(props: TeamMemberProps): JSX.Element {
  const {
    id,
    firstName,
    lastName,
    title,
    bio,
    picture,
    direction = 'vertical',
    className,
    hasDialog = false,
    hideInfo = false,
  } = props
  const [isDialogVisible, setDialogVisibility] = useState<boolean>()
  const image = picture && resolveAsImage(picture)
  const headingType = direction === 'vertical' ? 'sm' : 'body'
  const dialogRef = useRef(null)
  useClickOutside(dialogRef, () => {
    setDialogVisibility(false)
  })
  return (
    <div
      itemScope
      itemType="https://schema.org/Person"
      className={classNames(
        styles.teamMember,
        styles[`direction__${direction}`],
        { [styles.active]: isDialogVisible },
        className
      )}
    >
      <span
        className={classNames(styles.link, { [styles.linkActive]: hasDialog })}
        onClick={() => {
          if (hasDialog) {
            setDialogVisibility(true)
          }
        }}
      >
        {image && (
          <div className={classNames(styles.image, 'teamMemberImage')}>
            <GatsbyImage
              image={image}
              alt={`${firstName} ${lastName}`}
              className={classNames(
                styles.profilePicture,
                'teamMemberProfilePicture'
              )}
              loading="lazy"
            />
          </div>
        )}
        {!hideInfo && (
          <div className={styles.content}>
            <Heading className={styles.fullName} type={headingType} level={2}>
              <span itemProp="fullName">{`${firstName} ${lastName}`}</span>
            </Heading>
            {title && (
              <FreeText color="secondary" className={styles.title}>
                <span itemProp="jobTitle">{title}</span>
              </FreeText>
            )}
          </div>
        )}
      </span>
      {isDialogVisible && hasDialog && (
        <Dialog
          className={styles.dialog}
          isOpen={isDialogVisible}
          onClose={() => {
            setDialogVisibility(false)
            return false
          }}
        >
          <div className={styles.dialogContent} ref={dialogRef}>
            {image && (
              <header>
                <GatsbyImage
                  image={image}
                  alt={`${firstName} ${lastName}`}
                  className={styles.dialogImage}
                />
                <Heading level={2} type="md">
                  {firstName} {lastName}
                </Heading>
                <Caption type="md" className={styles.title}>
                  {title}
                </Caption>
              </header>
            )}
            <FreeText type="md" innerHTML={bio} className={styles.bio}>
              {bio}
            </FreeText>
          </div>
        </Dialog>
      )}
    </div>
  )
}
